import { QueryResult, useQuery } from '@apollo/client';
import GET_USAGE_CLICK_MAP from '../api/getUsageClickMap';
import { UNKNOWN_ROUTE } from '../services/routePattern';
import type { UsageClickMapData, UsageClickMapVariables } from '../types';

/**
 * RITA-1522 (Nutzungserfassung Phase 3d) — Klickkarte der aktuellen Seite.
 *
 * Optionen wie bei den beiden Abfragen der Auswertungsseite: `network-only`, weil verdichtete
 * Zahlen aus dem Cache beim nächsten Öffnen falsch aussähen, und `errorPolicy: 'none'`, weil
 * jedes Wurzelfeld der Lese-API non-null ist — ein werfender Resolver liefert ohnehin
 * `data: null` für das ganze Dokument, ein Teilergebnis gibt es nicht.
 *
 * `skip` ist hier neu und wichtig: das Overlay hängt über JEDER Seite. Ohne skip liefe die
 * Abfrage bei jedem Seitenwechsel, auch mit ausgeschaltetem Overlay — je eine Audit-Zeile
 * (operationLogPlugin) und eine Serveroperation, für niemanden. Übersprungen wird auch ohne
 * Routen-Muster: 'unbekannt' lehnt die API ab (assertClickRoute), und die Leiste sagt dann, dass
 * es für diese Seite keine Karte gibt.
 */
export interface UsageClickMapArgs {
  /** Overlay eingeschaltet — nur dann wird überhaupt gefragt. */
  enabled: boolean;
  /** Routen-Muster der aktuellen Seite (services/routePattern.ts). */
  route: string;
  /** Repräsentative Breite der gewählten Fensterklasse. */
  viewportWidth: number;
  themeLayout: string;
  themeStretch: boolean;
  days: number;
}

export const useUsageClickMap = ({
  enabled,
  route,
  viewportWidth,
  themeLayout,
  themeStretch,
  days,
}: UsageClickMapArgs): QueryResult<UsageClickMapData, UsageClickMapVariables> =>
  useQuery<UsageClickMapData, UsageClickMapVariables>(GET_USAGE_CLICK_MAP, {
    variables: { route, viewportWidth, themeLayout, themeStretch, range: { days } },
    fetchPolicy: 'network-only',
    errorPolicy: 'none',
    notifyOnNetworkStatusChange: true,
    skip: !enabled || route === UNKNOWN_ROUTE || route === '',
  });

export default useUsageClickMap;
